import {
  getPhotos as apiGetPhotos,
  getPhoto as apiGetPhoto,
  updateDescription as apiUpdateDescription,
  updateTrashed as apiUpdateTrashed,
  updateTitle as apiUpdateTitle,
  updateAuthor as apiUpdateAuthor,
  updatePfFileId as apiUpdatePfFileId,
  updateTags as apiUpdateTags,
  updateLocations as apiUpdateLocations,
  updatePhotoAlbum as apiUpdatePhotoAlbum,
} from '@client-shared/api/photos.api.js'
import filterFunctions from '@client-shared/utils/filter-functions'
import getPhotoGroupFunction from '@client-shared/utils/get-photo-group-function'
import getPhotoSortFunction from '@client-shared/utils/get-photo-sort-function'
import ignoreCanceledRequest from '@client-shared/utils/ignore-canceled-request.js'

import constants from '@/config/constants.js'

const fetchAbortControllers = {}

export default {
  namespaced: true,

  state: () => {
    return {
      list: [],
      // Project id this store's `list` was last fetched for. Lets the socket
      // reconnect catch-up skip stores that have never been loaded for the
      // current project (see cliesrc/utils/refetch-project-data.js).
      loadedProjectId: null,
      markedPhotoId: undefined,

      listStyle: constants.PHOTO_LIST_STYLES.GRID_LG, // Valid values: [constants.PHOTO_LIST_STYLES.GRID_LG, constants.PHOTO_LIST_STYLES.GRID_SM]
      showMetadata: false,
      groupBy: constants.PHOTOS_LIST_GROUPING.GROUP_BY_NONE,
      sortProperty: constants.SORT_PROPERTIES.PHOTO_NEWEST,

      // Only relevant for modus where photos can be selected
      selection: {
        selectedPhotoIds: [],
        shouldKeepSelected: true,
      },
    }
  },

  getters: {
    // Checked for every photo of the list while selected photos are kept: a Set instead of an
    // includes over the whole selection
    selectedPhotoIdsSet: (state) => {
      return new Set(state.selection.selectedPhotoIds)
    },

    listCountUntrashed: (state, getters) => {
      return state.list
        .filter(photo => !photo.trashed)
        .length
    },

    getListMap: (state) => {
      return new Map(state.list.map(photo => [
        photo._id,
        photo,
      ]))
    },

    getById: (state, getters) => {
      return photoId => getters.getListMap.get(photoId)
    },

    trashedPhotos: (state, getters) => {
      return state.list.filter(photo => photo.trashed)
    },

    listFilteredByModus: (state, getters, rootState, rootGetters) => {
      const modus = rootState.photosListModus.modus

      if (modus === constants.LIST_MODUS.TRASH) {
        return getters.trashedPhotos
      } else if (modus === constants.LIST_MODUS.LIST || modus === constants.LIST_MODUS.PRINT || modus === constants.LIST_MODUS.MULTI_ACTION_DOWNLOAD) {
        return state.list.filter(photo => !photo.trashed)
      } else if (modus === constants.LIST_MODUS.MULTI_ACTION_EDIT) {
        return state.list.filter(photo => {
          return !photo.trashed && rootGetters['acl/isAuthorizedToUpdatePhoto'](photo)
        })
      }

      return []
    },

    listFiltered: (state, getters, rootState, rootGetters) => {
      if (!rootState.photosListModus.filterStoreName) {
        return []
      }

      const modus = rootState.photosListModus.modus

      return getters.listFilteredByModus.filter(photo => {
        if (
          (modus === constants.LIST_MODUS.MULTI_ACTION_EDIT || modus === constants.LIST_MODUS.MULTI_ACTION_DOWNLOAD || modus === constants.LIST_MODUS.PRINT) &&
          state.selection.shouldKeepSelected &&
          getters.selectedPhotoIdsSet.has(photo._id)
        ) {
          return true
        }

        return filterFunctions.photos.matchesFilters({
          photo,
          project: rootState.project.project,
          ...rootState[rootState.photosListModus.filterStoreName],
        })
      })
    },

    listFilteredSorted: (state, getters, rootState, rootGetters) => {
      const listFiltered = getters.listFiltered || []

      return [ ...listFiltered ].sort((a, b) => {
        return getters.groupFunction(a, b) || getters.sortFunction(a, b)
      })
    },

    groupFunction: (state, getters, rootState, rootGetters) => {
      return getPhotoGroupFunction({
        groupBy: state.groupBy,
        project: rootState.project.project,
        locationLayers: rootState.locationLayers.list,
      })
    },

    sortFunction: (state, getters, rootState) => {
      return getPhotoSortFunction({
        sortProperty: state.sortProperty,
        project: rootState.project.project,
        selectedPhotoIds: state.selection.selectedPhotoIds,
      })
    },

    markedPhoto: (state, getters) => {
      if (!state.markedPhotoId) {
        return
      }

      return getters.getById(state.markedPhotoId)
    },

    canUseLocationLayerGrouping: (state, getters, rootState, rootGetters) => {
      const hasSelectableLayers = rootGetters['locationLayers/listSelectable'].length > 0
      const hasPhotosWithLocationLayer = Boolean(state.list.find(photo => photo.locationLayerId))

      return hasSelectableLayers || hasPhotosWithLocationLayer
    },
  },

  mutations: {
    SET_PHOTOS (state, photos) {
      state.list = photos
    },

    SET_LOADED_PROJECT_ID (state, projectId) {
      state.loadedProjectId = projectId
    },

    SET_MARKED_PHOTO_ID (state, photoId) {
      state.markedPhotoId = photoId
    },

    ADD_PHOTO (state, photoToAdd) {
      const existingPhotoIndex = state.list.findIndex(photo => photo._id === photoToAdd._id)

      if (existingPhotoIndex !== -1) {
        const isUpdateNewer = new Date(photoToAdd.modified) > new Date(state.list[existingPhotoIndex].modified)

        if (isUpdateNewer) {
          state.list[existingPhotoIndex] = photoToAdd
        }
      } else {
        state.list.push(photoToAdd)
      }
    },

    REPLACE_PHOTO (state, updatedPhoto) {
      const existingPhotoIndex = state.list.findIndex(photo => photo._id === updatedPhoto._id)

      if (existingPhotoIndex !== -1) {
        const isUpdateNewer = new Date(updatedPhoto.modified) > new Date(state.list[existingPhotoIndex].modified)

        if (isUpdateNewer) {
          state.list[existingPhotoIndex] = updatedPhoto
        }
      }
    },

    RESET_SORTING (state) {
      state.sortProperty = constants.SORT_PROPERTIES.PHOTO_NEWEST
    },

    SET_SORT_PROPERTY (state, sortProperty) {
      state.sortProperty = sortProperty
    },

    SET_SHOW_METADATA (state, newValue) {
      state.showMetadata = newValue
    },

    SET_GROUP_BY (state, groupBy) {
      if (!Object.values(constants.PHOTOS_LIST_GROUPING)
        .includes(groupBy)) {
        console.error(`${groupBy} is not a valid grouping mode for photo list`)

        return
      }

      state.groupBy = groupBy
    },

    RESET_VIEW_SETTINGS (state) {
      state.listStyle = constants.PHOTO_LIST_STYLES.GRID_LG
      state.showMetadata = false
      state.groupBy = constants.PHOTOS_LIST_GROUPING.GROUP_BY_NONE
      state.sortProperty = constants.SORT_PROPERTIES.PHOTO_NEWEST
    },

    SET_LIST_STYLE (state, newValue) {
      const validValues = [
        constants.PHOTO_LIST_STYLES.GRID_LG,
        constants.PHOTO_LIST_STYLES.GRID_SM,
      ]

      if (!validValues.includes(newValue)) {
        throw new Error(`Invalid list style: ${newValue}. Allowed values: ${validValues.join(', ')}`)
      }

      state.listStyle = newValue
    },

    ADD_SELECTED_PHOTO_ID (state, photoId) {
      if (!state.selection.selectedPhotoIds.includes(photoId)) {
        state.selection.selectedPhotoIds.push(photoId)
      }
    },

    REMOVE_SELECTED_PHOTO_ID (state, photoId) {
      state.selection.selectedPhotoIds = state.selection.selectedPhotoIds.filter(id => id !== photoId)
    },

    SET_SHOULD_KEEP_SELECTED (state, shouldKeepSelected) {
      state.selection.shouldKeepSelected = shouldKeepSelected
    },

    RESET_SELECTION (state) {
      state.selection = {
        selectedPhotoIds: [],
        shouldKeepSelected: true,
      }
    },

    RESET_PHOTOS (state) {
      state.list = []
      // Kept in sync with `list`: the tab-unmount wipe must also clear the
      // loadedProjectId gate (socket-io TRIGGER_RELOAD_* handlers +
      // refetch-project-data on reconnect both key off this flag), otherwise
      // they'd refetch into a list the user just navigated away from.
      state.loadedProjectId = null
    },

    FULL_RESET (state) {
      state.list = []
      state.loadedProjectId = null
      state.markedPhotoId = undefined

      state.listStyle = constants.PHOTO_LIST_STYLES.GRID_LG
      state.showMetadata = false
      state.groupBy = constants.PHOTOS_LIST_GROUPING.GROUP_BY_NONE
      state.sortProperty = constants.SORT_PROPERTIES.PHOTO_NEWEST

      state.selection = {
        selectedPhotoIds: [],
        shouldKeepSelected: true,
      }
    },
  },

  actions: {
    async fetch ({ commit, rootState }, { projectId }) {
      if (fetchAbortControllers.fetchList instanceof AbortController) {
        fetchAbortControllers.fetchList.abort()
      }

      fetchAbortControllers.fetchList = new AbortController()

      const photos = await ignoreCanceledRequest(apiGetPhotos({
        signal: fetchAbortControllers.fetchList.signal,
        axios: this.$axios,
        projectId,
      }))

      // The project was switched while the request was in flight: drop the previous project's list
      if (!photos || rootState.project.requestedProjectId !== projectId) {
        return
      }

      commit('SET_PHOTOS', photos)
      commit('SET_LOADED_PROJECT_ID', projectId)
    },

    async fetchById ({ commit, rootState }, photoId) {
      const projectId = rootState.project.requestedProjectId

      if (fetchAbortControllers.fetchById instanceof AbortController) {
        fetchAbortControllers.fetchById.abort()
      }

      fetchAbortControllers.fetchById = new AbortController()

      const photo = await ignoreCanceledRequest(apiGetPhoto({
        signal: fetchAbortControllers.fetchById.signal,
        axios: this.$axios,
        photoId,
      }))

      // The project was switched while the request was in flight: drop the previous project's item
      if (!photo || rootState.project.requestedProjectId !== projectId) {
        return
      }

      commit('ADD_PHOTO', photo)
    },

    async updateDescription ({ commit }, {
      photo,
      description,
    }) {
      const updatedPhoto = await apiUpdateDescription({
        axios: this.$axios,
        photo,
        description,
      })

      commit('REPLACE_PHOTO', updatedPhoto)
    },

    async deletePhoto ({ commit }, { photo }) {
      const updatedPhoto = await apiUpdateTrashed({
        axios: this.$axios,
        photo,
        isTrashed: true,
      })

      commit('REPLACE_PHOTO', updatedPhoto)
    },

    async restorePhoto ({
      commit,
      rootGetters,
    }, { photo }) {
      const updatedPhoto = await apiUpdateTrashed({
        axios: this.$axios,
        photo,
        isTrashed: false,
      })

      commit('REPLACE_PHOTO', updatedPhoto)
    },

    async updateAuthor ({ commit }, {
      photo,
      newUserId,
      newRole,
    }) {
      const updatedPhoto = await apiUpdateAuthor({
        axios: this.$axios,
        photo,
        newUserId,
        newRole,
      })

      commit('REPLACE_PHOTO', updatedPhoto)
    },

    async updatePfFileId ({ commit }, {
      photo,
      pfFileId,
    }) {
      const updatedPhoto = await apiUpdatePfFileId({
        axios: this.$axios,
        photo,
        pfFileId,
      })

      commit('REPLACE_PHOTO', updatedPhoto)
    },

    async updatePhotoTags ({
      commit,
      dispatch,
    }, {
      item,
      newValue,
    }) {
      const updatedPhoto = await apiUpdateTags({
        axios: this.$axios,
        photo: item,
        tags: newValue,
      })

      await dispatch('project/fetch', item.projectId, { root: true })

      commit('REPLACE_PHOTO', updatedPhoto)
    },

    async updatePhotoLocations ({
      commit,
      rootGetters,
    }, {
      photo,
      location1,
      location2,
      location3,
      location4,
      locationLayerId,
      locationLayerVersionId,
      locationPins,
    }) {
      const updatedPhoto = await apiUpdateLocations({
        axios: this.$axios,
        photo,
        location1,
        location2,
        location3,
        location4,
        locationLayerId,
        locationLayerVersionId,
        locationPins,
      })

      commit('REPLACE_PHOTO', updatedPhoto)
    },

    async updatePhotoAlbum ({
      commit,
      dispatch,
    }, {
      photo,
      album,
    }) {
      const updatedPhoto = await apiUpdatePhotoAlbum({
        axios: this.$axios,
        photo,
        album,
      })

      await dispatch('project/fetch', photo.projectId, { root: true })

      commit('REPLACE_PHOTO', updatedPhoto)
    },

    async updatePhotoTitle ({ commit }, {
      photo,
      title,
    }) {
      const updatedPhoto = await apiUpdateTitle({
        axios: this.$axios,
        photo,
        title,
      })

      commit('REPLACE_PHOTO', updatedPhoto)
    },
  },
}
